// What a forecast whose question has closed, with no answer yet, says about the search for one.
//
// Two sources describe the resolver's checks: its latest recorded check (`/api/resolve/report`,
// scheduled or on demand) and the row's stored on-demand result (`resolution`, written by
// "Check now"). The newer one wins. `next_check_at` and `checks_made` come from the resolver's
// check schedule; while the resolver does not send them the state cannot tell "more checks
// coming" from "stopped checking", and says neither.

import { resolutionMomentLocal } from "../formatDates.js";
import { isConclusiveResolutionStatus } from "../report/resolutionBody.js";
import { isConcluded } from "../tracking.js";
import { canEdit } from "./sharedRowGuards.js";
import { extractMultiway } from "./multiway.js";

const RUNNING = new Set(["running", "preprocessing"]);

/** The close moment of a forecast's question in ms, or null. */
export function closeMs(run) {
  const m = resolutionMomentLocal(run?.contract?.resolution_date, run?.question);
  return m && !Number.isNaN(m.getTime()) ? m.getTime() : null;
}

/**
 * Whether the viewer may press Check now on this forecast: anyone who may edit it (the resolve
 * route checks the same), except on a derived scenario branch, which is frozen. The active
 * forecast carries that flag as `forkDerived`; a stored row as `fork.derived`.
 */
export function mayCheckAnswer(run) {
  return !!run && !run.forkDerived && !run.fork?.derived && canEdit(run);
}

/** True once the question has closed and no answer (verdict or void) is in. */
export function awaitingAnswer(run, nowMs) {
  if (!run || run._pending || RUNNING.has(run.status) || isConcluded(run)) return false;
  const close = closeMs(run);
  return close != null && close <= nowMs;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whether the page's clock must keep moving while it sits idle for this forecast: from a day
 * before its close, so the close turns the answer box on, and while the closed question waits
 * for its answer, so "Last checked …" ages.
 */
export function answerClockNeeded(run, nowMs) {
  if (!run || run._pending || isConcluded(run)) return false;
  const close = closeMs(run);
  return close != null && close - nowMs < DAY_MS;
}

/** When `answerClockNeeded` turns on for this forecast (ms), or null when it never will. */
export function answerClockStartsAt(run) {
  if (!run || isConcluded(run)) return null;
  const close = closeMs(run);
  return close == null ? null : close - DAY_MS;
}

const ms = (iso) => {
  const t = Date.parse(iso || "");
  return Number.isFinite(t) ? t : null;
};

const list = (v) => (Array.isArray(v) ? v.filter((s) => typeof s === "string" && s.trim()) : []);

/** The next scheduled check: a time, null once checks have stopped, undefined when unknown. */
function nextCheckOf(attempt) {
  if (!("next_check_at" in attempt)) return undefined;
  if (attempt.next_check_at === null) return null;
  return ms(attempt.next_check_at) ?? undefined;
}

// A check counts toward the search for the answer only from shortly before the close. This
// browser closes a bare date at its own local midnight, while the resolver's first check fires at
// the end of the day in UTC, up to 14 hours earlier (UTC+14). An earlier check is Update now's
// resolver step before the question closed, and says nothing about the outcome since.
const CLOSE_SLACK_MS = 14 * 60 * 60 * 1000;

/**
 * The newest check since the close, from the resolver's record or the row's own on-demand
 * result. A check with no readable time is kept: it was made, just not when.
 */
function latestCheck(attempt, stored, close) {
  const since = (c) => (c && (c.at == null || c.at >= close - CLOSE_SLACK_MS) ? c : null);
  const fromReport = since(
    attempt && attempt.status === "unresolved"
      ? {
          at: ms(attempt.attempted_at),
          missing: list(attempt.missing_evidence),
          checked: list(attempt.open_reasons),
          nextAt: nextCheckOf(attempt),
          count: Number.isFinite(attempt.checks_made) ? attempt.checks_made : null,
        }
      : null,
  );
  const fromRow = since(
    stored && stored.status === "unresolved" && ms(stored.checked_at)
      ? {
          at: ms(stored.checked_at),
          missing: list(stored.missing_evidence),
          checked: list(stored.open_reasons),
          nextAt: undefined,
          count: null,
        }
      : null,
  );
  if (!fromReport) return fromRow;
  if (!fromRow || fromRow.at <= (fromReport.at ?? -Infinity)) return fromReport;
  // The on-demand result is newer; the schedule facts still come from the record.
  return { ...fromRow, nextAt: fromReport.nextAt, count: fromReport.count };
}

function dayOf(t) {
  return new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function timeOf(t) {
  return new Date(t).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/** "just now", "12 minutes ago", "today at 9:14 AM", "on Oct 29". */
function whenOf(t, nowMs) {
  const mins = Math.floor((nowMs - t) / 60000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins} minutes ago`;
  if (new Date(t).toDateString() === new Date(nowMs).toDateString()) return `today at ${timeOf(t)}`;
  return `on ${dayOf(t)}`;
}

/** "You can check again at 11:10 AM", the reason a refused or recent check gives. */
export function checkAgainText(atMs) {
  return `You can check again at ${timeOf(atMs)}`;
}

/**
 * Whether "Check now" can be pressed, and the reason when it cannot. The server stores when the
 * viewer may ask again (`resolution.check_again_at`, an hour after the last accepted check) and
 * refuses an earlier one.
 * @returns {{enabled: boolean, reason: string|null}}
 */
export function checkNowState(run, nowMs) {
  if (run?.resolution?.status === "in_flight")
    return { enabled: false, reason: "Sayer is checking now" };
  const again = ms(run?.resolution?.check_again_at);
  if (again != null && nowMs < again) return { enabled: false, reason: checkAgainText(again) };
  return { enabled: true, reason: null };
}

/**
 * The state of the search for an answer, or null when the forecast is not waiting for one.
 *
 * @param {object} run the forecast
 * @param {{attempt?: object|null, readFailed?: boolean, nowMs: number}} args `attempt` is the
 *   resolver's latest check; `readFailed` says the record could not be read, so nothing is
 *   claimed about checks the row itself does not record. The record holds yes/no checks only
 *   (the resolver's report skips multi-outcome attempts), so for a multi-outcome question an
 *   empty record claims nothing either.
 * @returns {null | {
 *   kind: "checking_now"|"not_checked"|"not_found"|"no_answer"|"unknown"|"found",
 *   label: string, detail: string, next: string|null,
 *   missing: string[], checked: string[], lastCheckedAt: number|null, nextCheckAt: number|null,
 * }}
 */
export function answerCheckState(run, { attempt = null, readFailed = false, nowMs }) {
  if (!awaitingAnswer(run, nowMs)) return null;
  const close = closeMs(run);
  const check = latestCheck(attempt, run.resolution, close);
  const base = {
    missing: check?.missing ?? [],
    checked: check?.checked ?? [],
    lastCheckedAt: check?.at ?? null,
    nextCheckAt: check?.nextAt ?? null,
  };
  // A close is the end of its day (the next local midnight), so name the day it ended.
  const closed = `Closed ${dayOf(close - 1)}`;
  // The resolver has an answer this row has not picked up yet: the report shows it.
  if (isConclusiveResolutionStatus(attempt?.status)) {
    return {
      ...base,
      kind: "found",
      label: "Answer found",
      detail: "Sayer has confirmed the outcome. Open the report to see it.",
      next: null,
    };
  }
  if (run.resolution?.status === "in_flight") {
    return {
      ...base,
      kind: "checking_now",
      label: "Checking now",
      detail: "Sayer is looking for the answer. This takes about a minute.",
      next: null,
    };
  }
  if (!check) {
    return readFailed || extractMultiway(run.contract)
      ? {
          ...base,
          kind: "unknown",
          label: "No answer yet",
          detail: "No outcome has been confirmed yet.",
          next: closed,
        }
      : {
          ...base,
          kind: "not_checked",
          label: "Checking for the answer",
          detail: "No outcome has been confirmed yet. Sayer will check shortly.",
          next: closed,
        };
  }
  const checks = (n) => `${n} ${n === 1 ? "check" : "checks"}`;
  // A record with no readable time still counts as a check; it just names no time.
  const lastChecked = check.at != null ? `Last checked ${whenOf(check.at, nowMs)}` : null;
  if (check.nextAt === null) {
    return {
      ...base,
      kind: "no_answer",
      label: "No answer found",
      detail: "No outcome could be confirmed by reliable sources. Sayer has stopped checking.",
      next: [lastChecked, check.count ? checks(check.count) : null].filter(Boolean).join(" · "),
    };
  }
  const next =
    check.nextAt != null && check.nextAt > nowMs ? `Next check ${dayOf(check.nextAt)}` : null;
  return {
    ...base,
    kind: "not_found",
    label: "Answer not found yet",
    detail: "An outcome has not yet been confirmed by reliable sources.",
    next: [lastChecked, next, check.count ? `${checks(check.count)} so far` : null]
      .filter(Boolean)
      .join(" · "),
  };
}
